/* GiftNote cart adapter: observed app-140 (169297745a48ffc7...) and app-141 (753129567a7ef3b2...).
 * Not a public vendor API. Keep the integration fixture when requalifying that asset.
 * This owns only a single cart-to-GiftNote handoff, not a general modal stack.
 */
class GiftNoteCartSession {
  static parts() {
    const host = document.querySelector('giftnote-core');
    const wrapper = host?.modal;
    const modal = host?.shadowRoot?.getElementById('giftnote-modal');
    const content = host?.shadowRoot?.getElementById('content');
    const trap = wrapper?.focus_trap;
    const legacy = typeof wrapper?.modal?.hide === 'function' && typeof wrapper.modal.isVisible === 'function';
    const current = typeof wrapper?.hideContainer === 'function' && typeof wrapper.showContainer === 'function' &&
      typeof wrapper.close === 'function' && typeof wrapper.isVisible === 'boolean' &&
      typeof wrapper.isOpening === 'boolean' && typeof wrapper.isClosing === 'boolean';
    if (!modal || !content || wrapper?.container !== modal || wrapper.content !== content ||
      typeof wrapper.open !== 'function' || (!legacy && !current) ||
      typeof trap?.deactivate !== 'function' || typeof trap.active !== 'boolean' ||
      typeof trap.pause !== 'function' || typeof trap.unpause !== 'function') return null;
    const lifecycle = legacy ? wrapper.modal : {
      isVisible: () => wrapper.isVisible,
      hide: () => wrapper.hideContainer()
    };
    return { host, wrapper, modal, content, trap, lifecycle };
  }

  static deepActive() {
    let element = document.activeElement;
    while (element?.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
    return element;
  }

  static parent(element) {
    return element.parentElement || element.getRootNode()?.host;
  }

  static usable(element) {
    if (!element?.isConnected || typeof element.focus !== 'function' || element.matches(':disabled')) return false;
    for (let node = element; node; node = GiftNoteCartSession.parent(node)) {
      if (node.inert || node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return element.getClientRects().length > 0;
  }

  constructor(cart, parts, resume, abandon) {
    Object.assign(this, parts);
    this.cart = cart;
    this.resume = resume;
    this.abandon = abandon;
    this.inert = new Map();
    this.semantics = new Map();
    this.stopped = false;
    this.initialFocusDone = false;
    const active = GiftNoteCartSession.deepActive();
    this.opener = Array.from(cart.querySelectorAll('giftnote-button')).some(host =>
      host === active || host.shadowRoot?.contains(active)) ? active : null;
    this.scroll = ['[data-scrollable]', 'cart-drawer-items'].map(selector => {
      const node = cart.querySelector(selector);
      return { selector, node, top: node?.scrollTop || 0, left: node?.scrollLeft || 0 };
    });
    this.handleKey = event => {
      if (event.key === 'Escape' && document.querySelector('dialog:modal')) {
        event.stopImmediatePropagation();
        return;
      }
      if (event.key === 'Escape') this.escapeEvent = event;
    };
    this.handleFocus = () => {
      const active = GiftNoteCartSession.deepActive();
      if (this.content.contains(active)) this.lastGiftFocus = active;
    };
    this.handleNativeClose = () => queueMicrotask(() => this.check());
    this.observer = new MutationObserver(() => this.check());
  }

  start() {
    this.protect();
    document.addEventListener('keydown', this.handleKey, true);
    this.host.shadowRoot.addEventListener('focusin', this.handleFocus, true);
    document.addEventListener('close', this.handleNativeClose, true);
    this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    this.observer.observe(this.host.shadowRoot, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });
    // The inspected open command calls show() synchronously. This checkpoint also
    // recovers an ignored/aborted command; it is NOT a guessed animation duration.
    queueMicrotask(() => this.check());
  }

  protect() {
    const wanted = new Set();
    let current = this.host;
    while (current && current !== document.body) {
      const parent = current.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach(sibling => {
        if (sibling !== current && !sibling.matches('dialog:modal')) wanted.add(sibling);
      });
      current = parent;
    }
    // Reconcile rather than re-snapshotting: a reparented host must not inherit
    // one of this adapter's old coarse inert boundaries.
    this.inert.forEach((wasInert, element) => {
      if (!wanted.has(element)) {
        element.inert = wasInert;
        this.inert.delete(element);
      }
    });
    wanted.forEach(element => {
      if (!this.inert.has(element)) this.inert.set(element, element.inert);
      element.inert = true;
    });
    const dialog = this.cart.id === 'sidebar-cart' ? this.cart : this.cart.querySelector('.drawer__inner');
    if (dialog && !this.semantics.has(dialog)) {
      this.semantics.set(dialog, ['role', 'aria-modal'].map(name => [name, dialog.getAttribute(name)]));
      dialog.removeAttribute('role');
      dialog.removeAttribute('aria-modal');
    }
    // Do not change AIMEE aria-hidden: its CSS uses it as the VISUAL open state.
    this.scroll.forEach(entry => {
      const replacement = this.cart.querySelector(entry.selector);
      if (replacement && replacement !== entry.node) {
        replacement.scrollTop = entry.top;
        replacement.scrollLeft = entry.left;
        entry.node = replacement;
      }
    });
  }

  check() {
    if (this.stopped) return;
    if (this.cancelled) {
      if (!this.wrapper.isVisible && !this.wrapper.isClosing && !this.lifecycle.isVisible()) {
        this.stop();
        this.afterCancel?.();
      }
      return;
    }
    if (!this.cart.isConnected) {
      this.cancel(() => this.abandon(this));
      return;
    }
    if (!this.host.isConnected || this.host.shadowRoot.getElementById('giftnote-modal') !== this.modal) {
      this.cancel(() => this.resume(this));
      return;
    }
    this.protect();
    const hidden = this.modal.hidden || this.modal.classList.contains('hidden') ||
      getComputedStyle(this.modal).display === 'none';
    if (hidden && !this.wrapper.isVisible && !this.wrapper.isOpening &&
      !this.wrapper.isClosing && !this.lifecycle.isVisible()) {
      // Base L.hide() (notably body-capture Escape) can bypass ht.close().
      // A cleared gn-overflow-hidden class alone does NOT establish this cleanup.
      this.trap.deactivate({ returnFocus: false });
      if (this.trap.active) return;
      this.stop();
      this.resume(this);
      return;
    }
    if (document.querySelector('dialog:modal')) {
      if (this.trap.active && !this.trap.paused) {
        this.trap.pause();
        this.pausedForNative = true;
      }
      return;
    }
    if (this.pausedForNative) {
      this.pausedForNative = false;
      const returnFocus = this.lastGiftFocus;
      if (this.trap.active) this.trap.unpause();
      if (!hidden && this.wrapper.isVisible && !this.wrapper.isClosing && GiftNoteCartSession.usable(returnFocus)) {
        returnFocus.focus({ preventScroll: true });
      }
    }
    if (!hidden && this.wrapper.isVisible && !this.wrapper.isClosing && !this.initialFocusDone) {
      const active = GiftNoteCartSession.deepActive();
      if (this.content.contains(active)) {
        this.initialFocusDone = true;
      } else {
        const target = this.host.shadowRoot.getElementById('close-button');
        if (GiftNoteCartSession.usable(target)) {
          target.focus({ preventScroll: true });
          this.initialFocusDone = GiftNoteCartSession.deepActive() === target;
        }
      }
    }
  }

  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.observer.disconnect();
    document.removeEventListener('keydown', this.handleKey, true);
    this.host.shadowRoot.removeEventListener('focusin', this.handleFocus, true);
    document.removeEventListener('close', this.handleNativeClose, true);
    this.inert.forEach((wasInert, element) => { element.inert = wasInert; });
    this.inert.clear();
    this.semantics.forEach((attributes, element) => attributes.forEach(([name, value]) => {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }));
    this.semantics.clear();
  }

  cancel(afterCancel) {
    if (this.stopped) return;
    // If ht.close() already scheduled its hide, drain that actual lifecycle.
    // Forcing hide now would leave its uncancellable timer free to hide a later
    // cycle and strip a new owner's overflow class. No extra timeout is guessed.
    this.trap.deactivate({ returnFocus: false });
    if (this.wrapper.isClosing && this.wrapper.isVisible) {
      this.cancelled = true;
      this.afterCancel = afterCancel;
      document.removeEventListener('keydown', this.handleKey, true);
      return;
    }
    if (this.wrapper.isVisible || this.lifecycle.isVisible()) this.lifecycle.hide();
    this.stop();
    afterCancel?.();
  }

  restoreGiftFocus(fallback) {
    const replacement = Array.from(this.cart.querySelectorAll('giftnote-button'))
      .map(host => host.shadowRoot?.querySelector('button'))
      .find(GiftNoteCartSession.usable);
    const target = GiftNoteCartSession.usable(this.opener) ? this.opener : replacement || fallback;
    if (GiftNoteCartSession.usable(target)) target.focus({ preventScroll: true });
    this.scroll.forEach(entry => {
      const node = this.cart.querySelector(entry.selector);
      if (node) { node.scrollTop = entry.top; node.scrollLeft = entry.left; }
    });
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.focusSession = 0;
    this.handleDocumentKeyDown = this.onDocumentKeyDown.bind(this);
    this.refreshBackgroundInert = () => {
      if (!this.giftnoteSession) setExternalContentInert(this);
    };
    this.handleCartLinkClick = this.onCartLinkClick.bind(this);
    this.handleCartLinkKeyDown = this.onCartLinkKeyDown.bind(this);
    this.handleOverlayClick = this.close.bind(this);
    this.handleCartRerender = this.onCartRerender.bind(this);
    this.handleGiftnoteOpen = this.suspendForGiftnote.bind(this);
  }

  connectedCallback() {
    // Capture runs before the vendor's document listener, even when registered later.
    document.addEventListener('giftnote_open', this.handleGiftnoteOpen, true);
    if (!this.cartRerenderUnsubscriber) {
      this.cartRerenderUnsubscriber = subscribe('cart::rerender', this.handleCartRerender);
    }
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('giftnote_open', this.handleGiftnoteOpen, true);
    this.cartRerenderUnsubscriber?.();
    this.cartRerenderUnsubscriber = null;
    this.unbindOverlay();
    this.unbindCartLink();
    clearTimeout(this.renderTimeout);
    this.renderTimeout = null;
    this.teardownCartDrawer();
  }

  onCartRerender(event) {
    if (!event) return;
    this.renderContents(event.cartData);
    if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (this.cartLink === cartLink) return;

    this.unbindCartLink();
    if (!cartLink) return;

    this.cartLink = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', this.handleCartLinkClick);
    cartLink.addEventListener('keydown', this.handleCartLinkKeyDown);
  }

  unbindCartLink() {
    if (!this.cartLink) return;
    this.cartLink.removeEventListener('click', this.handleCartLinkClick);
    this.cartLink.removeEventListener('keydown', this.handleCartLinkKeyDown);
    this.cartLink = null;
  }

  onCartLinkClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  onCartLinkKeyDown(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;
    event.preventDefault();
    this.open(event.currentTarget);
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (this.overlay === overlay) return;

    this.unbindOverlay();
    if (!overlay) return;
    this.overlay = overlay;
    overlay.addEventListener('click', this.handleOverlayClick);
  }

  unbindOverlay() {
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.overlay = null;
  }

  open(triggeredBy) {
    // Keep renderContents() and other late callbacks from reclaiming GiftNote's dialog.
    if (!this.isConnected || this.giftnoteSession || document.body.classList.contains('gn-overflow-hidden')) return;
    if (this.classList.contains('active') && this.focusActive && trappedOwner === this &&
      trappedContainer === this.querySelector('.drawer__inner')) return;
    document.querySelectorAll('product-modal[open]').forEach((modal) => modal.hide());
    this.focusSession += 1;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.cancelPendingOpen();
    removeTrapFocus();
    this.focusActive = false;
    setExternalContentInert(this);
    this.backgroundObserver = this.backgroundObserver || new MutationObserver(this.refreshBackgroundInert);
    this.backgroundObserver.disconnect();
    this.backgroundObserver.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    setDocumentOverflowHidden(this, 'overflow-hidden');

    const drawer = this.querySelector('.drawer__inner');
    const openingSession = this.focusSession;
    const finishOpening = () => {
      if (!this.isConnected || this.giftnoteSession || this.focusSession !== openingSession ||
        !this.classList.contains('active') || this.focusActive ||
        (trappedOwner && trappedOwner !== this)) return;
      const currentDrawer = this.querySelector('.drawer__inner');
      if (!isElementVisible(currentDrawer)) return;
      trapFocus(currentDrawer, currentDrawer, this);
      if (!currentDrawer.contains(document.activeElement)) return;
      this.focusActive = true;
      this.cancelPendingOpen();
    };

    this.openTransitionHandler = (event) => {
      if (event.target === drawer && event.propertyName === 'transform') finishOpening();
    };
    this.openTransitionElement = drawer;
    drawer.addEventListener('transitionend', this.openTransitionHandler);

    this.openTimeout = setTimeout(() => {
      this.openTimeout = null;
      this.classList.add('animate', 'active');
      this.openFrame = requestAnimationFrame(() => {
        this.openFrame = null;
        finishOpening();
      });
    });
    this.openFallback = setTimeout(finishOpening, 500);
  }

  close() {
    if (this.giftnoteSession) return;
    if (!this.classList.contains('active') && !this.openTimeout && !this.openFrame) return;

    this.focusSession += 1;
    const returnFocus = this.getReturnFocusElement();
    this.cancelPendingOpen();
    this.classList.remove('active');
    this.focusActive = false;
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    this.backgroundObserver?.disconnect();
    removeExternalContentInert(this);
    removeTrapFocus(returnFocus, trappedOwner ? this : null);
    removeDocumentOverflowHidden(this, 'overflow-hidden');
    this.activeElement = null;
  }

  teardownCartDrawer() {
    this.giftnoteSession?.cancel();
    this.giftnoteSession = null;
    this.focusSession += 1;
    this.cancelPendingOpen();
    this.classList.remove('active');
    this.focusActive = false;
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    this.backgroundObserver?.disconnect();
    removeExternalContentInert(this);
    removeTrapFocus(null, this);
    // A prior handoff may already have released this cart's lock.
    // A later disconnect must not remove the external dialog's identically named class.
    if (documentOverflowOwners.get('overflow-hidden')?.has(this)) {
      removeDocumentOverflowHidden(this, 'overflow-hidden');
    }
    this.activeElement = null;
  }

  cancelPendingOpen() {
    clearTimeout(this.openTimeout);
    clearTimeout(this.openFallback);
    if (this.openFrame) cancelAnimationFrame(this.openFrame);
    this.openTimeout = null;
    this.openFallback = null;
    this.openFrame = null;
    if (this.openTransitionHandler) {
      this.openTransitionElement?.removeEventListener('transitionend', this.openTransitionHandler);
      this.openTransitionHandler = null;
      this.openTransitionElement = null;
    }
  }

  getReturnFocusElement() {
    if (this.activeElement?.isConnected) return this.activeElement;
    if (this.activeElement?.id) {
      const replacement = document.getElementById(this.activeElement.id);
      if (replacement) return replacement;
    }
    return document.querySelector('#cart-icon-bubble');
  }

  canRestoreUpdateFocus(session) {
    return !document.querySelector('dialog:modal') && !this.giftnoteSession && this.isConnected && this.focusSession === session && this.classList.contains('active') &&
      isElementVisible(this.querySelector('.drawer__inner')) && (!trappedOwner || trappedOwner === this);
  }

  onDocumentKeyDown(event) {
    if (document.querySelector('dialog:modal') || this.giftnoteSession || event === this.giftnoteEscapeEvent) return;
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    event.preventDefault();
    this.close();
  }

  suspendForGiftnote(event) {
    if (!this.isConnected ||
      (!this.classList.contains('active') && !this.openTimeout && !this.openFrame)) return;
    if (this.giftnoteSession) {
      event.stopImmediatePropagation();
      if (!this.giftnoteSession.cancelled) this.giftnoteSession.wrapper.open();
      return;
    }
    const parts = GiftNoteCartSession.parts();
    if (!parts) {
      event.stopImmediatePropagation();
      console.error('GiftNote cart adapter: unsupported vendor modal; requalification required');
      return;
    }
    this.giftnoteSession = new GiftNoteCartSession(this, parts,
      this.resumeFromGiftnote.bind(this), this.teardownCartDrawer.bind(this));
    this.focusSession += 1;
    this.cancelPendingOpen();
    // Complete any already-requested visual opening, without close()/open().
    this.classList.add('animate', 'active');
    this.focusActive = false;
    document.removeEventListener('keydown', this.handleDocumentKeyDown);
    this.backgroundObserver?.disconnect();
    removeTrapFocus(null, this);
    removeExternalContentInert(this);
    // Keep the cart's documentOverflowOwners entry throughout the handoff.
    this.giftnoteSession.start();
    // Only the current host opens; retired vendor document listeners are bypassed.
    event.stopImmediatePropagation();
    parts.wrapper.open();
  }

  resumeFromGiftnote(session) {
    if (this.giftnoteSession !== session) return;
    this.giftnoteSession = null;
    if (!this.isConnected || !this.classList.contains('active')) return;
    this.focusSession += 1;
    this.giftnoteEscapeEvent = session.escapeEvent;
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    setExternalContentInert(this);
    this.backgroundObserver = this.backgroundObserver || new MutationObserver(this.refreshBackgroundInert);
    this.backgroundObserver.observe(document.body, { childList: true, subtree: true });
    // L.hide() removes this body class without knowing about theme owners.
    setDocumentOverflowHidden(this, 'overflow-hidden');
    document.addEventListener('keydown', this.handleDocumentKeyDown);
    const drawer = this.querySelector('.drawer__inner');
    if (!drawer) { this.teardownCartDrawer(); return; }
    trapFocus(drawer, drawer, this);
    this.focusActive = true;
    session.restoreGiftFocus(drawer.querySelector('.drawer__close') || drawer);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const renderSession = this.focusSession;
    clearTimeout(this.renderTimeout);
    this.renderTimeout = setTimeout(() => {
      this.renderTimeout = null;
      if (!this.isConnected) return;
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
      if (this.giftnoteSession || this.focusSession !== renderSession) return;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
